import { AfterViewInit, Component, OnInit } from '@angular/core';
import { Options } from 'ng5-slider';
import { LazyLoadingService } from '../../shared/misc/service/lazy-loading';
import { interval, Subscription } from 'rxjs';
import { SessionManager } from '../../session/service/session-manager';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SetupForm } from './setup.form';
import { HttpClient } from '@angular/common/http';
import { LocalStorageService } from '../../shared/storage/local-storage.service';
import { environment } from '../../../environments/environment';
import { AdyenPlugin, IAdyenPluginOptions } from '../../shared/payment/adyen/plugin';
import { Observable } from 'rxjs/Observable';
import { ISelectOption } from '../../shared/form/element/base-select/select-option.interface';
import { resolveUserLocale } from '../../shared/util/resolve-user-locale';


@Component({
  selector: 'setup',
  templateUrl: './setup.component.html',
  styleUrls: [ './setup.component.scss' ]
})
export class SetupComponent implements OnInit, AfterViewInit {

  savingInProgress: boolean = false;
  error: string = '';
  user: any = {};
  paymentSubscription: Subscription;
  customerLicense: any;
  license: any = { license: {}, pricing: [] };
  adyenOriginKey = environment.adyen.originKey;
  adyenCheckout: any;
  cardValid: boolean = false;
  cardEncryptedData: any = {};

  step1Promise: Subscription;
  step2Promise: Subscription;
  savePromise: Subscription;

  form: FormGroup;
  countries$: Observable<any>;
  currentStep = 1;
  isCompleted = false;

  photosLimit: number = 0;
  printsLimit: number = 0;
  photosCost: number = 0;
  printsCost: number = 0;
  printLicensePricing: any = {};
  photoLicensePricing: any = {};
  cost: number = 0;
  stepsCompleted = { 1: false, 2: false, 3: false };

  photosOptions: Options = {
    floor: 0,
    stepsArray: [],
    showTicks: true,
    showTicksValues: true
  };
  printsOptions: Options = {
    floor: 0,
    stepsArray: [],
    showTicks: true,
    showTicksValues: true
  };

  constructor(private lazyLoading: LazyLoadingService,
              private sessionManager: SessionManager,
              private router: Router,
              private route: ActivatedRoute,
              private httpClient: HttpClient,
              private adyenPlugin: AdyenPlugin,
              private localStorageService: LocalStorageService,
              private formBuilder: SetupForm) {
    this.form = formBuilder.buildForm();
  }

  ngOnInit() {
    this.loadLicense();
    this.loadCountries();
    this.loadCurrentSession();
  }

  ngAfterViewInit() {
  }

  isFirstStepValid() {
    if (!this.license.license.features) {
      return false;
    }

    if (!this.photosLimit) {
      return false;
    }
    if (this.license.license.features.indexOf('print') > -1) {
      if (!this.printsLimit) {
        return false;
      }
    }

    return true;
  }

  loadCurrentSession() {
    this.sessionManager.getIdentity()
      .subscribe((response) => {
        this.user = response;
        if (response.status === 'active' && response.customer.status === 'active') {
          this.stepsCompleted[1] = true;
          this.stepsCompleted[2] = true;
          this.stepsCompleted[3] = true;
          this.setCurrentStep(3);
          return;
        }
        this.checkPersistedSession();
        const data = {
          companyName: response.customer.name,
          email: response.email,
          phone: response.customer.phone,
          firstName: response.firstName,
          lastName: response.lastName,
          address: response.customer.address,
          city: response.customer.city,
          zipCode: response.customer.zipCode,
          countryCode: response.customer.countryCode,
        };
        this.form.patchValue(data);
      }, (err) => {
        if (err.status === 401) {
          this.router.navigate([ '/login' ]);
        }
      });
  }

  loadCountries() {
    this.countries$ = Observable.create((obs) => {
      this.httpClient.get<any>('/countries').subscribe(
        (response) => {
          obs.next(response.map((row) => {
            return <ISelectOption>{ value: row.key, label: row.name };
          }));
          obs.complete();
        },
        (error: any) => {
          obs.error(error);
          obs.complete();
        }
      );
    });
  }

  loadAdyen() {
    this.adyenPlugin.load(<IAdyenPluginOptions>{
      environment: environment.adyen.environment,
      adyenOriginKey: this.adyenOriginKey,
      onChange: (state, component) => this.onCardFormChange(state, component),
      locale: resolveUserLocale(),
      mountPoint: '.secured-fields'
    }).subscribe(() => {
    });
  }

  checkPersistedSession() {
    if (this.localStorageService.has('stp_st')) {
      const currentStep = this.localStorageService.get('stp_cs');
      const state = this.localStorageService.get('stp_st');
      let checkAttemptCounter = 0;
      const pullPaymentStatus = () => {
        this.httpClient.get(`/payment/${state.pid}`)
          .subscribe((response: any) => {
            if (response.status === 'completed') {
              this.isCompleted = !!state.c;
              this.stepsCompleted[1] = true;
              this.stepsCompleted[2] = true;
              this.stepsCompleted[3] = true;
              this.setCurrentStep(this.isCompleted ? 3 : currentStep);
            }
          }, (err) => {
            checkAttemptCounter++;
            if (checkAttemptCounter < 3) {
              pullPaymentStatus();
            }
          });
      };

      pullPaymentStatus();

      this.localStorageService.clear('stp_cs');
      this.localStorageService.clear('stp_st');
    }
  }


  loadLicense() {
    let license = this.localStorageService.get('lic');
    let photosLimit = this.localStorageService.get('licph');
    let printsLimit = this.localStorageService.get('licpr');

    this.httpClient.get(`/license/${license}`)
      .subscribe(
        (response) => {
          this.license = response;
          this.photosOptions = Object.assign(
            {},
            this.photosOptions,
            {
              stepsArray: this.license.pricing.filter(p => p.type === 'photo' && !p.isFree && p.limit > 0).map(p => {
                return { value: p.limit, legend: '' };
              })
            }
          );
          this.printsOptions = Object.assign(
            {},
            this.printsOptions,
            {
              stepsArray: this.license.pricing.filter(p => p.type === 'print' && !p.isFree && p.limit > 0).map(p => {
                return { value: p.limit, legend: '' };
              })
            }
          );
          if (this.license.license.features.indexOf('photo') > -1) {
            let pl = this.license.pricing.find(p => p.type === 'photo' && p.uuid === photosLimit);
            if (!pl) {
              pl = this.license.pricing.find(p => p.type === 'photo' && !p.isFree && p.limit > 0);
            }
            this.photosLimit = pl.limit;
          }
          if (this.license.license.features.indexOf('print') > -1) {
            let pl = this.license.pricing.find(p => p.type === 'print' && p.uuid === printsLimit);
            if (!pl) {
              pl = this.printsLimit = this.license.pricing.find(p => p.type === 'print' && !p.isFree && p.limit > 0);
            }
            this.printsLimit = pl.limit;
          }
          this.calculateCost();
        },
        (err) => {
        }
      );
  }

  calculateCost() {
    this.photoLicensePricing = this.license.pricing.find(p => {
      if (p.type === 'photo' && p.limit === this.photosLimit) {
        return p;
      }

      return false;
    });
    if (this.license.license.features && this.license.license.features.indexOf('print') > -1) {
      this.printLicensePricing = this.license.pricing.find(p => {
        if (p.type === 'print' && p.limit === this.printsLimit) {
          return p;
        }

        return false;
      });

      this.printsCost = +(this.printLicensePricing ? this.printLicensePricing.unitPrice * this.printLicensePricing.limit : 0).toFixed(2);
    }

    this.photosCost = +(this.photoLicensePricing ? this.photoLicensePricing.unitPrice * this.photoLicensePricing.limit : 0).toFixed(2);
    this.cost = +(this.photosCost + this.printsCost).toFixed(2);
  }

  persistState(state: any) {
    this.localStorageService.save('stp_cs', this.currentStep);
    this.localStorageService.save('stp_st', state);
  }

  navigateToFirstStep() {
    if (this.currentStep === 3) {
      return;
    }
    this.setCurrentStep(1);
  }

  navigateToSecondStep() {
    if (this.currentStep === 3) {
      return;
    }
    if (!this.stepsCompleted[1]) {
      return;
    }
    this.setCurrentStep(2);
  }

  setCurrentStep(step: number) {
    this.currentStep = step;
    if (step === 2) {
      setTimeout(() => this.loadAdyen(), 1);
    }
    if (step === 3) {
      setTimeout(() => {
        this.router.navigate([ '/campaign' ]);
      }, 2000);
    }
  }

  onCardFormChange(state, component) {
    this.cardEncryptedData = false;
    this.cardValid = state.isValid;
    if (state.isValid) {
      this.cardEncryptedData = state.data.paymentMethod;
    }
  }

  save() {
    this.savingInProgress = true;
    if (this.stepsCompleted[1]) {
      this.saveUser();
      return;
    }
    this.savePromise = this.httpClient.put('/license', {
      license: this.license.license.uuid,
      photoLicensePricing: this.photoLicensePricing.uuid,
      printLicensePricing: this.printLicensePricing ? this.printLicensePricing.uuid : undefined
    }).subscribe(
      (response: any) => {
        this.stepsCompleted[1] = true;
        this.saveUser();
      }, (response: any) => {
        this.savingInProgress = false;
        if (response.error.errors) {
          let error = '';
          response.error.errors.forEach(err => {
            Object.values(err.constraints).forEach(msg => {
              error += `${msg}<br />`;
            });
          });
          this.error = error;
        } else {
          this.error = response.error.message;
        }
      }
    );
  }

  saveUser() {
    this.savePromise = this.httpClient.post('/setup', {
      ...this.form.value,
      ...this.cardEncryptedData
    })
      .subscribe((response: any) => {
        if (response.redirectUrl) {
          this.persistState({ c: response.completed, pid: response.payment });
          window.location.href = response.redirectUrl;
        } else if (response.payment) {
          this.paymentSubscription = interval(5000)
            .subscribe(
              () => {
                this.getPaymentStatus(response.payment);
              }
            );
        } else {
          this.savingInProgress = false;
          this.setCurrentStep(3);
          this.stepsCompleted[2] = true;
          this.stepsCompleted[3] = true;
        }
      }, (response) => {
        this.savingInProgress = false;
        if (response.error.errors) {
          let error = '';
          response.error.errors.forEach(err => {
            Object.values(err.constraints).forEach(msg => {
              error += `${msg}<br />`;
            });
          });
          this.error = error;
        } else {
          this.error = response.error.message;
        }
      });
  }

  submit() {
    this.error = '';
    if (this.cost > 0 && (!this.cardEncryptedData)) {
      return;
    }
    if (this.form.invalid) {
      return;
    }

    this.save();
  }

  getPaymentStatus(uuid) {
    this.httpClient.get(`/payment/${uuid}`)
      .subscribe(
        (response: any) => {
          if (response.status === 'completed') {
            this.savingInProgress = false;
            this.paymentSubscription.unsubscribe();
            this.setCurrentStep(3);
            this.stepsCompleted[2] = true;
            this.stepsCompleted[3] = true;
          }
        }
      );
  }
}
